import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Skeleton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { FiActivity, FiClipboard, FiFileText } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "react-query";
import { getMyActivityApi } from "../../api/workers";
import attachmentKeys from "../../utils/attachmentKeys";
import FileList from "../../components/files/FileList";

const getRange = (period) => {
  if (period === "all") return {};
  const end = new Date();
  const start = new Date();
  if (period === "today") start.setHours(0, 0, 0, 0);
  if (period === "7days") start.setDate(start.getDate() - 6);
  if (period === "30days") start.setDate(start.getDate() - 29);
  if (period !== "today") start.setHours(0, 0, 0, 0);
  return { start: start.toISOString(), end: end.toISOString() };
};

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "Not available";

const patientName = (patient) => {
  if (!patient || typeof patient === "string") return "Unknown patient";
  return (
    `${patient.bioData?.surname || ""} ${patient.bioData?.otherNames || ""}`.trim() ||
    "Unknown patient"
  );
};

const workerName = (worker) => {
  if (!worker || typeof worker === "string") return "Unknown worker";
  return (
    `${worker.firstName || ""} ${worker.lastName || ""}`.trim() ||
    "Unknown worker"
  );
};

const currency = (value) => new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  minimumFractionDigits: 2,
}).format(Number(value) || 0);

const fieldValue = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (item && typeof item === "object") {
        return Object.entries(item)
          .filter(([, nested]) => nested !== "" && nested !== null && nested !== undefined)
          .map(([key, nested]) => `${attachmentKeys[key] || key}: ${fieldValue(nested)}`)
          .join(", ");
      }
      return fieldValue(item);
    }).filter(Boolean).join("; ") || "—";
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, nested]) => nested !== "" && nested !== null && nested !== undefined)
      .map(([key, nested]) => `${attachmentKeys[key] || key}: ${fieldValue(nested)}`)
      .join(", ") || "—";
  }
  return String(value);
};

const FormDetails = ({ form }) => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap="3">
    {Object.entries(form || {}).map(([key, value]) => (
      <Box key={key} p="3" bg="gray.50" borderRadius="lg" minW="0">
        <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase">
          {attachmentKeys[key] || key}
        </Text>
        <Text mt="1" fontSize="sm" whiteSpace="pre-wrap" overflowWrap="anywhere">{fieldValue(value)}</Text>
      </Box>
    ))}
  </SimpleGrid>
);

const PaymentDetails = ({ payment = {} }) => (
  <Box borderWidth="1px" borderColor={payment.paid ? "green.200" : "gray.200"} borderRadius="xl" overflow="hidden">
    <Flex px="4" py="3" bg={payment.paid ? "green.50" : "gray.50"} justify="space-between" align="center">
      <Text fontWeight="800" fontSize="sm">Payment details</Text>
      <Badge colorScheme={payment.paid ? "green" : "orange"}>{payment.paid ? "Paid" : "Not paid"}</Badge>
    </Flex>
    <SimpleGrid columns={{ base: 1, sm: 3 }} gap="3" p="4">
      <Box><Text fontSize="xs" color="gray.500">Full amount</Text><Text fontWeight="800">{currency(payment.fullAmount)}</Text></Box>
      <Box><Text fontSize="xs" color="gray.500">Discount</Text><Text fontWeight="800">{Number(payment.discount) || 0}%</Text></Box>
      <Box><Text fontSize="xs" color="gray.500">Total payable</Text><Text fontWeight="800">{currency(payment.totalAmount)}</Text></Box>
    </SimpleGrid>
    {payment.markedPaidBy && <Text px="4" pb="4" fontSize="xs" color="gray.500">Marked paid by {workerName(payment.markedPaidBy)} on {formatDate(payment.markedPaidAt)}</Text>}
  </Box>
);

const RequestFormActivityItem = ({ form }) => (
  <AccordionItem borderWidth="1px" borderRadius="xl" overflow="hidden">
    <AccordionButton px="4" py="4">
      <Flex flex="1" justify="space-between" gap="4" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} textAlign="left" minW="0">
        <Box minW="0">
          <HStack flexWrap="wrap">
            <Text fontWeight="800" textTransform="capitalize">{form.type} form</Text>
            <Badge colorScheme={form.deleted ? "gray" : "green"}>{form.deleted ? "Archived" : "Active"}</Badge>
            <Badge colorScheme={form.payment?.paid ? "green" : "orange"}>{form.payment?.paid ? "Paid" : "Not paid"}</Badge>
          </HStack>
          <Text mt="1" color="gray.500" fontSize="sm">{patientName(form.patient)} · Last activity {formatDate(form.lastActivityAt)}</Text>
        </Box>
        <Box textAlign={{ base: "left", md: "right" }}>
          <Text fontWeight="700" fontSize="sm">{currency(form.payment?.totalAmount)}</Text>
          <Text color="gray.500" fontSize="xs">Version {form.version || 1} · with {workerName(form.currentHolder)}</Text>
        </Box>
      </Flex>
      <AccordionIcon ml="3" />
    </AccordionButton>
    <AccordionPanel pt="0" px={{ base: "4", md: "5" }} pb="5">
      <Divider mb="5" />
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="3" mb="5">
        <Box bg="gray.50" p="3" borderRadius="lg"><Text fontSize="xs" color="gray.500">PATIENT ID</Text><Text mt="1" fontSize="sm" fontWeight="700">{form.patient?.patientNumber || form.patient?.bioData?.unitNo || "—"}</Text></Box>
        <Box bg="gray.50" p="3" borderRadius="lg"><Text fontSize="xs" color="gray.500">CREATED BY</Text><Text mt="1" fontSize="sm" fontWeight="700">{workerName(form.createdBy)}</Text><Text fontSize="xs" color="gray.500">{formatDate(form.createdAt)}</Text></Box>
        <Box bg="gray.50" p="3" borderRadius="lg"><Text fontSize="xs" color="gray.500">CURRENT HOLDER</Text><Text mt="1" fontSize="sm" fontWeight="700">{workerName(form.currentHolder)}</Text>{form.precedingHolder && <Text fontSize="xs" color="gray.500">From {workerName(form.precedingHolder)}</Text>}</Box>
        <Box bg="gray.50" p="3" borderRadius="lg"><Text fontSize="xs" color="gray.500">LAST UPDATED</Text><Text mt="1" fontSize="sm" fontWeight="700">{workerName(form.lastUpdatedBy)}</Text><Text fontSize="xs" color="gray.500">{formatDate(form.updatedAt)}</Text></Box>
      </SimpleGrid>

      <Heading size="sm" mb="3">Complete form content</Heading>
      <FormDetails form={form.form} />
      <Box mt="5"><PaymentDetails payment={form.payment} /></Box>
      {!!form.files?.length && <Box mt="5"><Heading size="sm" mb="2">Supporting files</Heading><FileList files={form.files} /></Box>}

      {!!form.events?.length && <Box mt="5"><Heading size="sm" mb="3">My activity on this form</Heading><Stack spacing="2">{form.events.map((event, index) => <Flex key={`${event.type}-${event.at}-${index}`} borderLeftWidth="3px" borderColor="blue.300" pl="3" py="1" justify="space-between" gap="3"><Box><Text fontSize="sm" fontWeight="700" textTransform="capitalize">{event.type}</Text>{event.version && <Text fontSize="xs" color="gray.500">Version {event.version}</Text>}</Box><Text fontSize="xs" color="gray.500">{formatDate(event.at)}</Text></Flex>)}</Stack></Box>}

      {!!form.transfers?.length && <Box mt="5"><Heading size="sm" mb="3">Transfer history</Heading><Stack spacing="2">{form.transfers.map((transfer) => <Box key={transfer._id} bg="gray.50" p="3" borderRadius="lg"><Text fontSize="sm"><Text as="span" fontWeight="700">{workerName(transfer.from)}</Text> sent it to <Text as="span" fontWeight="700">{workerName(transfer.to)}</Text></Text><Text mt="1" fontSize="xs" color="gray.500">Transferred by {workerName(transfer.transferredBy)} · {formatDate(transfer.transferredAt)}</Text></Box>)}</Stack></Box>}

      {!!form.allHolders?.length && <Box mt="5"><Text fontSize="xs" color="gray.500" fontWeight="800">ALL HOLDERS</Text><HStack mt="2" flexWrap="wrap">{form.allHolders.map((holder) => <Badge key={holder._id || holder}>{workerName(holder)}</Badge>)}</HStack></Box>}
      <Button as={RouterLink} to={`/patient/${form.patient?._id}`} mt="5" size="sm" variant="outline">View patient record</Button>
    </AccordionPanel>
  </AccordionItem>
);

const AssessmentActivityItem = ({ item }) => {
  const assessment = item.assessment;
  return (
    <AccordionItem borderWidth="1px" borderRadius="xl" overflow="hidden">
      <AccordionButton px="4" py="4">
        <Flex flex="1" justify="space-between" gap="4" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} textAlign="left">
          <Box>
            <HStack flexWrap="wrap">
              <Text fontWeight="800" textTransform="capitalize">{patientName(item.patient)}</Text>
              <Badge colorScheme={item.closed ? "gray" : "green"}>{item.closed ? "Closed attendance" : "Active attendance"}</Badge>
            </HStack>
            <Text mt="1" color="gray.500" fontSize="sm">Written {formatDate(assessment.createdAt)}</Text>
          </Box>
          <Box textAlign={{ base: "left", md: "right" }}>
            <Text fontSize="sm" fontWeight="700" noOfLines={2}>{assessment.assessment || "Clinical assessment"}</Text>
            <Text fontSize="xs" color="gray.500">{assessment.diagnoses?.length || 0} diagnoses · {assessment.examination?.length || 0} examinations</Text>
          </Box>
        </Flex>
        <AccordionIcon ml="3" />
      </AccordionButton>
      <AccordionPanel pt="0" px={{ base: "4", md: "5" }} pb="5">
        <Divider mb="5" />
        <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
          <Box bg="gray.50" p="4" borderRadius="xl"><Text fontSize="xs" color="gray.500" fontWeight="800">SIGNS AND SYMPTOMS</Text><Text mt="2" fontSize="sm" whiteSpace="pre-wrap">{assessment.signsAndSymptoms || "Not recorded"}</Text></Box>
          <Box bg="gray.50" p="4" borderRadius="xl"><Text fontSize="xs" color="gray.500" fontWeight="800">ASSESSMENT</Text><Text mt="2" fontSize="sm" whiteSpace="pre-wrap">{assessment.assessment || "Not recorded"}</Text></Box>
          <Box bg="gray.50" p="4" borderRadius="xl"><Text fontSize="xs" color="gray.500" fontWeight="800">PLAN</Text><Text mt="2" fontSize="sm" whiteSpace="pre-wrap">{assessment.plan || "Not recorded"}</Text></Box>
          <Box bg="gray.50" p="4" borderRadius="xl"><Text fontSize="xs" color="gray.500" fontWeight="800">AUTHOR</Text><Text mt="2" fontSize="sm" fontWeight="700">{workerName(assessment.createdBy)}</Text><Text fontSize="xs" color="gray.500">Created {formatDate(assessment.createdAt)}</Text>{assessment.updatedAt && assessment.updatedAt !== assessment.createdAt && <Text fontSize="xs" color="gray.500">Updated {formatDate(assessment.updatedAt)} by {workerName(assessment.updatedBy || assessment.createdBy)}</Text>}</Box>
        </SimpleGrid>

        {!!assessment.examination?.length && <Box mt="5"><Heading size="sm" mb="3">Examination findings</Heading><Stack spacing="2">{assessment.examination.map((entry) => <Box key={entry._id || `${entry.exam}-${entry.note}`} borderLeftWidth="3px" borderColor="blue.300" pl="3" py="1"><Text fontSize="sm" fontWeight="700">{entry.exam}</Text><Text fontSize="sm" color="gray.600">{entry.note || "No additional note"}</Text></Box>)}</Stack></Box>}

        {!!assessment.diagnoses?.length && <Box mt="5"><Heading size="sm" mb="3">Diagnoses</Heading><Stack spacing="2">{assessment.diagnoses.map((diagnosis) => <Flex key={diagnosis._id || diagnosis.name} bg="gray.50" p="3" borderRadius="lg" justify="space-between" align="flex-start" gap="3"><Box><Text fontSize="sm" fontWeight="800">{diagnosis.name}</Text>{diagnosis.note && <Text mt="1" fontSize="sm" color="gray.600">{diagnosis.note}</Text>}</Box><Badge colorScheme={diagnosis.verificationStatus === "confirmed" ? "green" : diagnosis.verificationStatus === "refuted" ? "red" : "orange"}>{diagnosis.verificationStatus || "provisional"}</Badge></Flex>)}</Stack></Box>}

        <Button as={RouterLink} to={`/appointment/${item.attendanceId}`} mt="5" size="sm" variant="outline">View attendance</Button>
      </AccordionPanel>
    </AccordionItem>
  );
};

const SummaryCard = ({ icon, label, value, color }) => (
  <Box borderWidth="1px" borderRadius="xl" p="4" bg="white">
    <HStack spacing="3">
      <Center
        boxSize="10"
        borderRadius="lg"
        bg={`${color}.50`}
        color={`${color}.600`}
      >
        {icon}
      </Center>
      <Box>
        <Text color="gray.500" fontSize="sm">
          {label}
        </Text>
        <Text fontSize="2xl" fontWeight="800">
          {value || 0}
        </Text>
      </Box>
    </HStack>
  </Box>
);

const WorkerActivityHistory = () => {
  const [period, setPeriod] = useState("today");
  const range = useMemo(() => getRange(period), [period]);
  const query = useQuery(
    ["getMyActivityApi", period],
    () => getMyActivityApi(range),
    { keepPreviousData: true },
  );
  const activity = query.data?.data;
  const attendances = activity?.attendances || [];
  const requestForms = activity?.requestForms || [];
  const assessments = attendances.flatMap((attendance) =>
    (attendance.assessments || []).map((assessment) => ({
      assessment,
      attendanceId: attendance._id,
      patient: attendance.patient,
      closed: attendance.closed,
      openedAt: attendance.openedAt,
    })),
  ).sort((a, b) => new Date(b.assessment.createdAt) - new Date(a.assessment.createdAt));

  return (
    <Box
      mt="6"
      maxW="1100px"
      mx="auto"
      w="full"
      bg="white"
      borderRadius="2xl"
      boxShadow="card"
      overflow="hidden"
    >
      <Flex
        p={{ base: "5", md: "6" }}
        justify="space-between"
        gap="4"
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
      >
        <Box>
          <Heading size="md">My work activity</Heading>
          <Text mt="1" color="gray.500" fontSize="sm">
            Attendances, assessments and request forms you worked on.
          </Text>
        </Box>
        <HStack spacing="2" flexWrap="wrap">
          {[
            ["today", "Today"],
            ["7days", "7 days"],
            ["30days", "30 days"],
            ["all", "All history"],
          ].map(([value, label]) => (
            <Button
              key={value}
              size="sm"
              variant={period === value ? "solid" : "outline"}
              onClick={() => setPeriod(value)}
            >
              {label}
            </Button>
          ))}
        </HStack>
      </Flex>

      <Box px={{ base: "5", md: "6" }} pb="5">
        {query.isLoading ? (
          <SimpleGrid columns={{ base: 1, md: 3 }} gap="4">
            {[1, 2, 3].map((item) => (
              <Skeleton key={item} h="92px" borderRadius="xl" />
            ))}
          </SimpleGrid>
        ) : query.isError ? (
          <Center minH="160px" flexDirection="column">
            <Text fontWeight="700">Activity history could not be loaded</Text>
            <Button
              mt="3"
              size="sm"
              variant="outline"
              onClick={() => query.refetch()}
            >
              Try again
            </Button>
          </Center>
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap="4" mb="5">
              <SummaryCard
                icon={<FiActivity />}
                label="Attendances handled"
                value={activity?.summary?.attendances}
                color="blue"
              />
              <SummaryCard
                icon={<FiClipboard />}
                label="Assessments written"
                value={activity?.summary?.assessments}
                color="green"
              />
              <SummaryCard
                icon={<FiFileText />}
                label="Request forms handled"
                value={activity?.summary?.requestForms}
                color="purple"
              />
            </SimpleGrid>

            <Tabs colorScheme="blue" isLazy>
              <TabList overflowX="auto">
                <Tab whiteSpace="nowrap">
                  Attendances ({attendances.length})
                </Tab>
                <Tab whiteSpace="nowrap">
                  Assessments ({assessments.length})
                </Tab>
                <Tab whiteSpace="nowrap">
                  Request forms ({requestForms.length})
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel px="0" pb="0">
                  {!attendances.length ? (
                    <Center
                      minH="150px"
                      borderWidth="1px"
                      borderStyle="dashed"
                      borderRadius="xl"
                    >
                      <Text color="gray.500">
                        No attendance activity in this period.
                      </Text>
                    </Center>
                  ) : (
                    <Accordion allowMultiple>
                      <Stack spacing="3">
                        {attendances.map((attendance) => (
                          <AccordionItem
                            key={attendance._id}
                            borderWidth="1px"
                            borderRadius="xl"
                            overflow="hidden"
                          >
                            <AccordionButton py="4" px="4">
                              <Flex
                                flex="1"
                                justify="space-between"
                                gap="3"
                                align={{ base: "flex-start", md: "center" }}
                                direction={{ base: "column", md: "row" }}
                                textAlign="left"
                              >
                                <Box>
                                  <HStack>
                                    <Text
                                      fontWeight="800"
                                      textTransform="capitalize"
                                    >
                                      {patientName(attendance.patient)}
                                    </Text>
                                    <Badge
                                      colorScheme={
                                        attendance.closed ? "gray" : "green"
                                      }
                                    >
                                      {attendance.closed ? "Closed" : "Active"}
                                    </Badge>
                                  </HStack>
                                  <Text mt="1" color="gray.500" fontSize="xs">
                                    Last activity{" "}
                                    {formatDate(attendance.lastActivityAt)}
                                  </Text>
                                </Box>
                                <HStack>
                                  <Badge>
                                    {attendance.stages.length} stages
                                  </Badge>
                                  <Badge colorScheme="blue">
                                    {attendance.assessments.length} assessments
                                  </Badge>
                                </HStack>
                              </Flex>
                              <AccordionIcon ml="3" />
                            </AccordionButton>
                            <AccordionPanel pt="0" pb="4">
                              <SimpleGrid
                                columns={{ base: 1, md: 2 }}
                                gap="3"
                                my="3"
                              >
                                <Box bg="gray.50" p="3" borderRadius="lg">
                                  <Text
                                    color="gray.500"
                                    fontSize="xs"
                                    fontWeight="700"
                                  >
                                    ATTENDANCE OPENED
                                  </Text>
                                  <Text mt="1" fontSize="sm">
                                    {formatDate(attendance.openedAt)}
                                  </Text>
                                </Box>
                                <Box bg="gray.50" p="3" borderRadius="lg">
                                  <Text
                                    color="gray.500"
                                    fontSize="xs"
                                    fontWeight="700"
                                  >
                                    MY HANDLING STAGES
                                  </Text>
                                  <Text mt="1" fontSize="sm">
                                    {attendance.stages.length ||
                                      "Assessment only"}
                                  </Text>
                                </Box>
                              </SimpleGrid>
                              {!!attendance.assessments.length && (
                                <Stack spacing="2" mb="4">
                                  {attendance.assessments.map((assessment) => (
                                    <Box
                                      key={assessment._id}
                                      borderLeftWidth="3px"
                                      borderColor="blue.300"
                                      pl="3"
                                      py="1"
                                    >
                                      <Text fontSize="sm" fontWeight="700">
                                        {assessment.assessment ||
                                          "Clinical assessment"}
                                      </Text>
                                      <Text color="gray.500" fontSize="xs">
                                        {formatDate(assessment.createdAt)}
                                      </Text>
                                    </Box>
                                  ))}
                                </Stack>
                              )}
                              <Button
                                as={RouterLink}
                                to={`/appointment/${attendance._id}`}
                                size="sm"
                                variant="outline"
                              >
                                View attendance
                              </Button>
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Stack>
                    </Accordion>
                  )}
                </TabPanel>
                <TabPanel px="0" pb="0">
                  {!assessments.length ? (
                    <Center minH="150px" borderWidth="1px" borderStyle="dashed" borderRadius="xl">
                      <Text color="gray.500">No assessment activity in this period.</Text>
                    </Center>
                  ) : (
                    <Accordion allowMultiple>
                      <Stack spacing="3">
                        {assessments.map((item) => <AssessmentActivityItem key={item.assessment._id} item={item} />)}
                      </Stack>
                    </Accordion>
                  )}
                </TabPanel>
                <TabPanel px="0" pb="0">
                  {!requestForms.length ? (
                    <Center
                      minH="150px"
                      borderWidth="1px"
                      borderStyle="dashed"
                      borderRadius="xl"
                    >
                      <Text color="gray.500">
                        No request-form activity in this period.
                      </Text>
                    </Center>
                  ) : (
                    <>
                    <Accordion allowMultiple>
                      <Stack spacing="3">
                        {requestForms.map((form) => <RequestFormActivityItem key={form._id} form={form} />)}
                      </Stack>
                    </Accordion>
                    <Stack spacing="3" display="none">
                      {requestForms.map((form) => (
                        <Flex
                          key={form._id}
                          borderWidth="1px"
                          borderRadius="xl"
                          p="4"
                          justify="space-between"
                          gap="4"
                          align={{ base: "flex-start", md: "center" }}
                          direction={{ base: "column", md: "row" }}
                        >
                          <Box>
                            <HStack flexWrap="wrap">
                              <Text fontWeight="800" textTransform="capitalize">
                                {form.type} form
                              </Text>
                              {form.deleted && <Badge>Archived</Badge>}
                            </HStack>
                            <Text mt="1" color="gray.500" fontSize="sm">
                              {patientName(form.patient)} · Last activity{" "}
                              {formatDate(form.lastActivityAt)}
                            </Text>
                          </Box>
                          <Box textAlign={{ base: "left", md: "right" }}>
                            <HStack
                              justify={{ md: "flex-end" }}
                              flexWrap="wrap"
                            >
                              {form.events.slice(0, 4).map((event, index) => (
                                <Badge
                                  key={`${event.type}-${event.at}-${index}`}
                                  colorScheme={
                                    event.type === "edited"
                                      ? "blue"
                                      : event.type === "sent"
                                        ? "purple"
                                        : "green"
                                  }
                                >
                                  {event.type}
                                </Badge>
                              ))}
                            </HStack>
                            <Text mt="2" color="gray.500" fontSize="xs">
                              Currently with {workerName(form.currentHolder)}
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </Stack>
                    </>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </>
        )}
      </Box>
    </Box>
  );
};

export default WorkerActivityHistory;
